import React, { createRef } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import BlockModal from '../../../../../components/BlockModal/BlockModal';
import moment from 'moment';

import AddIcon from '@material-ui/icons/Add';

import { withStyles } from '@material-ui/core/styles';
import styles from './styles.js';

import {
  getString,
  stripInvalidCharacters,
} from '../../../../../helpers/stringHelpers.js';
import { iPhoneFriendlyButtonLabel } from '../../../../../helpers/stringHelpers';
import constants from '../../../../../constants.js';
const stringsComponent = 'consentedPersons';

const WAIT_INTERVAL = 250;
const NAME_FIELD_MAX_LENGTH = 50;
const RELATIONSHIP_FIELD_MAX_LENGTH = 50;
const ADDRESS_FIELD_MAX_LENGTH = 120;
const PHONE_FIELD_MAX_LENGTH = 20;

// Max age of conented party. Used by date field.
const MAX_CONSENTED_PARTY_AGE = 120;

class ConsentedPersonsDialog extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      dialogData: {
        name: '',
        relationship: '',
        address: '',
        phone: '',
        dateOfBirth: '',
        dateOfBirthDay: '',
        dateOfBirthMonth: '',
        dateOfBirthYear: '',
        confirm: false,
      },
      dirty: {
        name: false,
        relationship: false,
        address: false,
        phone: false,
        dateOfBirth: false,
        dateOfBirthDay: false,
        dateOfBirthMonth: false,
        dateOfBirthYear: false,
      },
      nameError: false,
      relationshipError: false,
      addressError: false,
      phoneError: false,
      dateOfBirthError: false,
      error: false,
      blockModalOpen: false,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleDateFieldChange = this.handleDateFieldChange.bind(this);
    this.handleDateFieldKeyDown = this.handleDateFieldKeyDown.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleBlockModalCancel = this.handleBlockModalCancel.bind(this);
    this.handleBlockModalConfirm = this.handleBlockModalConfirm.bind(this);
    this.validate = this.validate.bind(this);

    this.dateOfBirthDayRef = createRef();
    this.dateOfBirthMonthRef = createRef();
    this.dateOfBirthYearRef = createRef();
  }

  componentDidMount() {
    this.timer = null;
  }

  handleChange(event, callback = null) {
    clearTimeout(this.timer);
    const target = event.target.name;
    const value =
      target === 'confirm' ? event.target.checked : event.target.value;

    const newDialogData = { ...this.state.dialogData };
    newDialogData[target] = value;

    const newDirty = { ...this.state.dirty };
    if (target !== 'confirm') {
      newDirty[target] = true;
    }

    this.setState(
      {
        dialogData: newDialogData,
        dirty: newDirty,
      },
      () => {
        if (typeof callback === 'function') {
          callback(newDirty, newDialogData);
        }
      }
    );

    if (target !== 'confirm') {
      this.setState({ error: true }); // We set an error, so the button is disabled until validation is run
      this.timer = setTimeout(this.validate, WAIT_INTERVAL);
    }
  }

  handleDateFieldChange(event) {
    // Call the usual field change event with an additional callback to set
    // the 'dateOfBirth' state from the three combined dateOfBirth fields.
    this.handleChange(event, (newDirty, newDialogData) => {
      if (
        newDirty.dateOfBirthDay &&
        newDirty.dateOfBirthMonth &&
        newDirty.dateOfBirthYear
      ) {
        const { dateOfBirthDay, dateOfBirthMonth, dateOfBirthYear } =
          newDialogData;

        this.setState({
          dialogData: {
            ...newDialogData,
            dateOfBirth: `${dateOfBirthDay}/${dateOfBirthMonth}/${dateOfBirthYear}`,
          },
          dirty: {
            ...newDirty,
            dateOfBirth: true,
          },
        });
      }
    });

    const field = event.currentTarget.name;
    const value = event.currentTarget.value;
    switch (field) {
      case 'dateOfBirthDay':
        if (value.length === 2 && value >= 1 && value <= 31) {
          this.dateOfBirthMonthRef.current.focus();
        }
        break;
      case 'dateOfBirthMonth':
        if (value.length === 2 && value >= 1 && value <= 12) {
          this.dateOfBirthYearRef.current.focus();
        }
        break;
    }
  }

  handleDateFieldKeyDown(event) {
    // Allow 'backspace' key to go back to a previous date field if the
    // current value is empty.
    if (event.currentTarget.value == '' && event.keyCode == 8) {
      const field = event.currentTarget.name;
      switch (field) {
        case 'dateOfBirthMonth':
          this.dateOfBirthDayRef.current.focus();
          break;
        case 'dateOfBirthYear':
          this.dateOfBirthMonthRef.current.focus();
          break;
      }
    }
  }

  handleCancel() {
    const { handleCancel } = this.props;
    const { dirty } = this.state;

    if (Object.values(dirty).includes(true)) {
      this.setState({
        blockModalOpen: true,
      });
    } else {
      handleCancel();
    }
  }

  handleBlockModalCancel() {
    this.setState({
      blockModalOpen: false,
    });
  }

  handleBlockModalConfirm() {
    const { handleCancel } = this.props;

    this.setState({ blockModalOpen: false });
    handleCancel();
  }

  validate() {
    const phoneReg = constants.PHONE_REGEX;

    const { dialogData, dirty } = this.state;

    const dateOfBirth = moment(
      dialogData.dateOfBirth.trim(),
      ['DD/MM/YYYY', 'D/M/YYYY'],
      true
    );

    const nameError =
      dirty.name && stripInvalidCharacters(dialogData.name).length === 0
        ? true
        : false;
    const relationshipError =
      dirty.relationship &&
      stripInvalidCharacters(dialogData.relationship).length === 0
        ? true
        : false;
    const addressError =
      dirty.address && stripInvalidCharacters(dialogData.address).length === 0
        ? true
        : false;
    const phoneError =
      dirty.phone && !phoneReg.test(dialogData.phone.trim()) ? true : false;
    const dateOfBirthError =
      dirty.dateOfBirth &&
      (!dateOfBirth.isValid() ||
        !dateOfBirth.isBetween(
          moment().subtract(MAX_CONSENTED_PARTY_AGE, 'years'),
          moment()
        ))
        ? true
        : false;

    const error =
      nameError ||
      relationshipError ||
      addressError ||
      phoneError ||
      dateOfBirthError;

    this.setState({
      nameError: nameError,
      relationshipError: relationshipError,
      addressError: addressError,
      phoneError: phoneError,
      dateOfBirthError: dateOfBirthError,
      error: error,
    });
  }

  render() {
    const { classes, open, handleCancel, handleSubmit } = this.props;

    const {
      dialogData,
      nameError,
      relationshipError,
      addressError,
      phoneError,
      dateOfBirthError,
      error,
      blockModalOpen,
    } = this.state;

    const saveButtonDisabled =
      error ||
      !dialogData.confirm ||
      !dialogData.name ||
      !dialogData.relationship ||
      !dialogData.address ||
      !dialogData.phone ||
      !dialogData.dateOfBirth;

    return (
      <div>
        <Dialog open={open}>
          <DialogTitle className={classes.title}>
            <div className={classes.heading}>
              <AddIcon className={classes.avatar} />
              <Typography variant='h3'>
                {getString(stringsComponent, 'DIALOG_TITLE_ADD')}
              </Typography>
            </div>
          </DialogTitle>
          <Divider />
          <br />
          <DialogContent>
            <Typography className={classes.formField}>
              {getString('app', 'ENGLISH_ONLY_TEXT')}
            </Typography>
            <br />
            <div className={classes.formField}>
              <Typography className={classes.label}>
                {getString(stringsComponent, 'LABEL_NAME')}
              </Typography>
              <TextField
                variant='outlined'
                placeholder='e.g. John Doe'
                inputProps={{
                  className: classes.textInput,
                  maxLength: NAME_FIELD_MAX_LENGTH,
                }}
                name='name'
                value={dialogData.name}
                onChange={this.handleChange}
              />
            </div>
            <Typography component='div' className={classes.validation}>
              {nameError
                ? getString(stringsComponent, 'NAME_REQUIRED_HELP_TEXT')
                : ''}
            </Typography>
            <br />
            <div className={classes.formField}>
              <Typography className={classes.label}>
                {getString(stringsComponent, 'LABEL_RELATIONSHIP')}
              </Typography>
              <TextField
                variant='outlined'
                placeholder='e.g. Son'
                inputProps={{
                  className: classes.textInput,
                  maxLength: RELATIONSHIP_FIELD_MAX_LENGTH,
                }}
                name='relationship'
                value={dialogData.relationship}
                onChange={this.handleChange}
              />
            </div>
            <Typography component='div' className={classes.validation}>
              {relationshipError
                ? getString(stringsComponent, 'RELATIONSHIP_REQUIRED_HELP_TEXT')
                : ''}
            </Typography>
            <br />
            <div className={classes.formField}>
              <Typography className={classes.label}>
                {getString(stringsComponent, 'LABEL_ADDRESS')}
              </Typography>
              <TextField
                variant='outlined'
                placeholder='e.g. 22 Street Lane, Citytown'
                inputProps={{
                  className: classes.textInput,
                  maxLength: ADDRESS_FIELD_MAX_LENGTH,
                }}
                name='address'
                value={dialogData.address}
                onChange={this.handleChange}
              />
            </div>
            <Typography component='div' className={classes.validation}>
              {addressError
                ? getString(stringsComponent, 'ADDRESS_REQUIRED_HELP_TEXT')
                : ''}
            </Typography>
            <br />
            <div className={classes.formField}>
              <Typography className={classes.label}>
                {getString(stringsComponent, 'LABEL_PHONE')}
              </Typography>
              <TextField
                variant='outlined'
                placeholder='e.g. 0211234567'
                inputProps={{
                  className: classes.textInput,
                  maxLength: PHONE_FIELD_MAX_LENGTH,
                }}
                name='phone'
                value={dialogData.phone}
                onChange={this.handleChange}
              />
            </div>
            <Typography component='div' className={classes.validation}>
              {phoneError
                ? getString(stringsComponent, 'PHONE_REQUIRED_HELP_TEXT')
                : ''}
            </Typography>
            <br />
            <div className={classes.formField}>
              <Typography className={classes.label}>
                {getString(stringsComponent, 'LABEL_DOB')}
              </Typography>
              <div className={classes.dateOfBirthFieldContainer}>
                <TextField
                  variant='outlined'
                  placeholder='DD'
                  inputProps={{
                    className: `${classes.textInput} ${classes.dayInput}`,
                    patern: '[0-9]*',
                    inputMode: 'numeric',
                    maxLength: 2,
                    onKeyDown: this.handleDateFieldKeyDown,
                  }}
                  name='dateOfBirthDay'
                  value={dialogData.dateOfBirthDay}
                  onChange={this.handleDateFieldChange}
                  inputRef={this.dateOfBirthDayRef}
                />
                <TextField
                  variant='outlined'
                  placeholder='MM'
                  inputProps={{
                    className: `${classes.textInput} ${classes.monthInput}`,
                    patern: '[0-9]*',
                    inputMode: 'numeric',
                    maxLength: 2,
                    onKeyDown: this.handleDateFieldKeyDown,
                  }}
                  name='dateOfBirthMonth'
                  value={dialogData.dateOfBirthMonth}
                  onChange={this.handleDateFieldChange}
                  inputRef={this.dateOfBirthMonthRef}
                  className={classes.monthInputContainer}
                />
                <TextField
                  variant='outlined'
                  placeholder='YYYY'
                  inputProps={{
                    className: `${classes.textInput} ${classes.yearInput}`,
                    patern: '[0-9]*',
                    inputMode: 'numeric',
                    maxLength: 4,
                    onKeyDown: this.handleDateFieldKeyDown,
                  }}
                  name='dateOfBirthYear'
                  value={dialogData.dateOfBirthYear}
                  onChange={this.handleDateFieldChange}
                  inputRef={this.dateOfBirthYearRef}
                />
              </div>
            </div>
            <Typography component='div' className={classes.validation}>
              {dateOfBirthError
                ? getString(stringsComponent, 'DOB_REQUIRED_HELP_TEXT')
                : ''}
            </Typography>
            <br />
            <div className={classes.formField}>
              <FormControlLabel
                control={
                  <Checkbox
                    defaultChecked={this.state.confirm}
                    name='confirm'
                    onChange={this.handleChange}
                  />
                }
                label={getString(stringsComponent, 'LABEL_CONFIRM')}
                className={classes.checkboxInput}
              />
            </div>
          </DialogContent>
          <DialogActions className={classes.dialogActions}>
            <Button variant='outlined' onClick={this.handleCancel}>
              {getString('contactDetails', 'CANCEL_LABEL')}
            </Button>
            <Button
              variant='outlined'
              color='primary'
              disabled={saveButtonDisabled}
              onClick={() => handleSubmit(this.state.dialogData)}
            >
              {iPhoneFriendlyButtonLabel(
                getString('contactDetails', 'SAVE_LABEL'),
                saveButtonDisabled
              )}
            </Button>
          </DialogActions>
        </Dialog>
        <BlockModal
          show={blockModalOpen}
          message={getString('blockModal', 'MESSAGE')}
          cancel={this.handleBlockModalCancel}
          ok={this.handleBlockModalConfirm}
          secondaryButtonLabel={getString('blockModal', 'BUTTON_LABEL_CANCEL')}
        />
      </div>
    );
  }
}

export default withStyles(styles, { withTheme: true })(ConsentedPersonsDialog);
